import { useRef, useState } from 'react'
import { Button } from '@agms/ui'
import {
  STATEMENT_PAGE_ACCEPT,
  STATEMENT_PAGE_LIMIT,
  STATEMENT_PAGE_TOO_LARGE,
  STATEMENT_PAGE_TOO_MANY,
  STATEMENT_PAGE_WRONG_TYPE,
  statementPageIssue,
  statementPageIssueMessage,
  type StatementPageIssue,
} from '../../lib/statementPages'

export interface StoredStatementPage {
  id?: string
  file_name: string
  mime_type: string
  file_size: number
  position?: number
}

interface StagedPage {
  key: string
  file: File
  issue: StatementPageIssue | null
}

interface StatementPagePickerProps {
  storedPages: StoredStatementPage[]
  readerOff: boolean
  readPending: boolean
  deletePending: boolean
  statusMessage?: string | null
  onUpload: (files: File[], onProgress: (percent: number) => void) => Promise<void>
  onRead: () => void
  onDeleteStored: () => void
}

function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`
  return `${(size / (1024 * 1024)).toFixed(1)} MB`
}

export function StatementPagePicker({
  storedPages,
  readerOff,
  readPending,
  deletePending,
  statusMessage,
  onUpload,
  onRead,
  onDeleteStored,
}: StatementPagePickerProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const sequence = useRef(0)
  const [staged, setStaged] = useState<StagedPage[]>([])
  const [notice, setNotice] = useState<string | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState<number | null>(null)

  const issues = staged.some((row) => row.issue != null)
  const ready = staged.filter((row) => row.issue == null).map((row) => row.file)

  function noticeFor(rows: StagedPage[]): string | null {
    const messages: string[] = []
    if (rows.some((row) => row.issue === 'size')) messages.push(STATEMENT_PAGE_TOO_LARGE)
    if (rows.some((row) => row.issue === 'type')) messages.push(STATEMENT_PAGE_WRONG_TYPE)
    return messages.length > 0 ? messages.join(' ') : null
  }

  function addFiles(incoming: File[]) {
    if (incoming.length === 0 || uploading) return
    if (staged.length + incoming.length > STATEMENT_PAGE_LIMIT) {
      setNotice(STATEMENT_PAGE_TOO_MANY)
      return
    }
    const next = [
      ...staged,
      ...incoming.map((file) => {
        sequence.current += 1
        return {
          key: `staged-${sequence.current}`,
          file,
          issue: statementPageIssue(file),
        }
      }),
    ]
    setStaged(next)
    setNotice(noticeFor(next))
  }

  function move(index: number, direction: -1 | 1) {
    setStaged((current) => {
      const target = index + direction
      if (target < 0 || target >= current.length) return current
      const copy = current.slice()
      const [row] = copy.splice(index, 1)
      copy.splice(target, 0, row)
      return copy
    })
  }

  function remove(key: string) {
    const next = staged.filter((row) => row.key !== key)
    setStaged(next)
    setNotice(noticeFor(next))
  }

  async function send(files: File[]) {
    setUploading(true)
    setProgress(null)
    setNotice(null)
    try {
      await onUpload(files, (percent) => setProgress(percent))
      setStaged([])
      setProgress(100)
    } catch {
      setProgress(null)
    } finally {
      setUploading(false)
    }
  }

  function chooseFiles() {
    if (!uploading) inputRef.current?.click()
  }

  function onUploadClick() {
    if (uploading || issues) return
    if (ready.length === 0) {
      chooseFiles()
      return
    }
    void send(ready)
  }

  return (
    <div>
      <h2 className="text-sm font-semibold text-gray-900">Statement file</h2>
      <p id="statement-page-rules" className="mt-1 text-xs text-gray-500">
        Upload one PDF or several photos of the same statement. JPEG, PNG, and HEIC are accepted.
        Each file must be 10 MB or smaller, and one statement can have up to 8 files.
        Arrange the pages, then upload. Read statement uses that order.
        A blurry photo has to be taken again.
        The files stay in private storage. Read statement proposes figures for you to confirm.
        Highlighted fields are low confidence. Confirm the figures before running the analysis.
        Statement files are removed after the configured retention period. The figures on this analysis stay.
      </p>

      <input
        ref={inputRef}
        className="sr-only"
        type="file"
        multiple
        accept={STATEMENT_PAGE_ACCEPT}
        aria-label="Statement pages"
        tabIndex={-1}
        onChange={(event) => {
          const selected = Array.from(event.target.files ?? [])
          event.target.value = ''
          addFiles(selected)
        }}
      />

      <div
        role="button"
        tabIndex={uploading ? -1 : 0}
        aria-disabled={uploading}
        aria-label="Add statement pages. Drag PDF, JPEG, PNG, or HEIC files here, or press Enter to choose files."
        data-drop-active={dragActive ? 'true' : 'false'}
        onClick={chooseFiles}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            chooseFiles()
          }
        }}
        onDragEnter={(event) => {
          event.preventDefault()
          if (uploading) return
          setDragActive(true)
        }}
        onDragOver={(event) => {
          event.preventDefault()
          if (uploading) return
          if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy'
          setDragActive(true)
        }}
        onDragLeave={(event) => {
          const next = event.relatedTarget
          if (next instanceof Node && event.currentTarget.contains(next)) return
          setDragActive(false)
        }}
        onDrop={(event) => {
          event.preventDefault()
          setDragActive(false)
          if (uploading) return
          addFiles(Array.from(event.dataTransfer?.files ?? []))
        }}
        className={`mt-3 rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 ${
          dragActive ? 'border-sky-500 bg-sky-50' : 'border-gray-300 bg-gray-50 hover:border-gray-400'
        } ${uploading ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
      >
        <p className="text-sm font-medium text-gray-900">Drag statement pages here</p>
        <p className="mt-1 text-xs text-gray-500">PDF, JPEG, PNG, or HEIC. Press Enter to choose files.</p>
      </div>

      {staged.length > 0 ? (
        <ol aria-label="Selected statement pages" className="mt-3 space-y-2">
          {staged.map((row, index) => (
            <li
              key={row.key}
              className={`flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2 ${
                row.issue ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
              }`}
            >
              <div>
                <p className="text-sm text-gray-900">
                  <span className="font-medium">{index + 1}.</span> {row.file.name}
                  <span className="ml-2 text-xs text-gray-500">{formatBytes(row.file.size)}</span>
                </p>
                {row.issue ? (
                  <p className="text-xs text-red-700">{statementPageIssueMessage(row.issue)}</p>
                ) : null}
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploading || index === 0}
                  aria-label={`Move ${row.file.name} up`}
                  onClick={() => move(index, -1)}
                >
                  Up
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploading || index === staged.length - 1}
                  aria-label={`Move ${row.file.name} down`}
                  onClick={() => move(index, 1)}
                >
                  Down
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={uploading}
                  aria-label={`Remove ${row.file.name}`}
                  onClick={() => remove(row.key)}
                >
                  Remove
                </Button>
              </div>
            </li>
          ))}
        </ol>
      ) : null}

      {notice ? (
        <p className="mt-3 text-sm text-red-700" role="alert">{notice}</p>
      ) : null}

      {uploading ? (
        <div className="mt-3" role="progressbar" aria-label="Statement upload progress" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progress ?? undefined} aria-valuetext={progress == null ? 'Uploading' : `${progress}%`}>
          <div className="h-2 overflow-hidden rounded bg-gray-200">
            <div className="h-2 rounded bg-sky-600 transition-all" style={{ width: `${progress ?? 12}%` }} />
          </div>
          <p className="mt-1 text-xs text-gray-600">{progress == null ? 'Uploading…' : `${progress}%`}</p>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Button
          type="button"
          onClick={onUploadClick}
          disabled={uploading || issues}
          isLoading={uploading}
          aria-describedby="statement-page-rules"
        >
          Upload statement
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={onRead}
          disabled={readPending || storedPages.length === 0 || readerOff}
        >
          Read statement
        </Button>
        {storedPages.length > 0 ? (
          <Button
            type="button"
            variant="outline"
            disabled={deletePending || uploading}
            onClick={() => {
              if (window.confirm('Delete this statement file? The analysis figures stay.')) {
                onDeleteStored()
              }
            }}
          >
            Delete statement
          </Button>
        ) : null}
      </div>

      {readerOff ? (
        <p className="mt-3 text-sm text-gray-700" role="status">
          Statement reading is not configured. Enter the volume, transaction count, card mix, and fees from the statement.
        </p>
      ) : null}

      {storedPages.length > 0 ? (
        <div className="mt-4">
          <p className="text-xs text-gray-500">Stored pages. A new upload replaces this set.</p>
          <ol aria-label="Stored statement pages" className="mt-2 list-decimal space-y-1 pl-5 text-sm text-gray-700">
            {storedPages.map((page, index) => (
              <li key={page.id ?? `${page.file_name}-${index}`}>{page.file_name}</li>
            ))}
          </ol>
        </div>
      ) : null}

      {statusMessage ? <p className="mt-3 text-sm text-gray-700" role="status">{statusMessage}</p> : null}
    </div>
  )
}
